/**
 * Logo Component
 * A type rectangle on a little 3D physics. Monochrome + theme-aware: black on light
 * pages, white on dark — no colour shift during the animation.
 *  - idle: a slow, graceful full flip, cycling its four type frames
 *  - hover: tilts forward for perspective and multiplies into three solid pages that
 *    fan open around the centre axis and spin together as one 3D form
 *  - press + drag: grab and rotate the form yourself; release throws it with inertia
 *  - tap (no drag): opens the menu (onClick)
 *
 * Each impression is DOUBLE-SIDED (a front face + a back face at rotateY(180)), so the
 * logo stays visible through the entire spin instead of culling away on the back half.
 */

import { useEffect, useRef, useState } from 'react';

interface LogoProps {
  onClick?: () => void;
  isOpen?: boolean;
}

const SPIN_FAST = 7;      // deg/frame while hovering (gentle)
const FRICTION = 0.95;
const DRAG_SENS = 0.6;
const IDLE_FLIP_MS = 6500;
const TILT = 16;          // forward tilt (deg) when active — more perspective
const BLADES = 3;         // solid pages that fan out together on hover
const REAL = 1;           // the centre (front-facing) page
const FAN = 60;           // angle each side page swings out to when multiplied

export function Logo({ onClick }: LogoProps) {
  const [frame, setFrame] = useState(1);
  const [hovering, setHovering] = useState(false);
  const inner = useRef<HTMLDivElement>(null);

  const rot = useRef(0);
  const vel = useRef(0);
  const tilt = useRef(0);
  const mode = useRef<'idle' | 'hover' | 'drag'>('idle');
  const over = useRef(false);
  const raf = useRef<number | null>(null);
  const running = useRef(false);

  const dStartX = useRef(0);
  const dStartRot = useRef(0);
  const dMoved = useRef(0);
  const dLastX = useRef(0);
  const downAt = useRef(0);

  const paint = () => {
    if (inner.current) {
      inner.current.style.transform = `scale(1.10) rotateX(${tilt.current}deg) rotateY(${rot.current}deg)`;
    }
    const turns = Math.floor((rot.current + 180) / 360);
    const f = (((turns % 4) + 4) % 4) + 1;
    setFrame((p) => (p === f ? p : f));
  };

  const run = () => {
    if (running.current) return;
    running.current = true;
    const tick = () => {
      const active = mode.current === 'hover' || mode.current === 'drag';
      tilt.current += ((active ? TILT : 0) - tilt.current) * 0.1;

      if (mode.current === 'drag') {
        paint();
        raf.current = requestAnimationFrame(tick);
        return;
      }
      if (mode.current === 'hover') {
        vel.current += (SPIN_FAST - vel.current) * 0.12;
      } else {
        vel.current *= FRICTION;
      }
      rot.current += vel.current;
      if (mode.current === 'idle' && Math.abs(vel.current) < 0.4 && Math.abs(tilt.current) < 0.25) {
        const target = Math.round(rot.current / 360) * 360;
        rot.current += (target - rot.current) * 0.2;
        if (Math.abs(target - rot.current) < 0.25) {
          rot.current = target;
          tilt.current = 0;
          paint();
          running.current = false;
          return;
        }
      }
      paint();
      raf.current = requestAnimationFrame(tick);
    };
    raf.current = requestAnimationFrame(tick);
  };

  useEffect(() => {
    const id = setInterval(() => {
      if (mode.current === 'idle') {
        vel.current += 14;
        run();
      }
    }, IDLE_FLIP_MS);
    return () => {
      clearInterval(id);
      if (raf.current) cancelAnimationFrame(raf.current);
    };
  }, []);

  const onEnter = () => {
    over.current = true;
    if (mode.current !== 'drag') { setHovering(true); mode.current = 'hover'; run(); }
  };
  const onLeave = () => {
    over.current = false;
    if (mode.current !== 'drag') { setHovering(false); if (mode.current === 'hover') mode.current = 'idle'; run(); }
  };
  const onDown = (e: React.PointerEvent) => {
    try { (e.currentTarget as Element).setPointerCapture?.(e.pointerId); } catch { /* non-capturable pointer */ }
    mode.current = 'drag';
    dStartX.current = e.clientX; dLastX.current = e.clientX; dStartRot.current = rot.current;
    dMoved.current = 0; vel.current = 0; downAt.current = Date.now();
    run();
  };
  const onMove = (e: React.PointerEvent) => {
    if (mode.current !== 'drag') return;
    const dx = e.clientX - dStartX.current;
    dMoved.current = Math.max(dMoved.current, Math.abs(dx));
    rot.current = dStartRot.current + dx * DRAG_SENS;
    vel.current = (e.clientX - dLastX.current) * DRAG_SENS;
    dLastX.current = e.clientX;
  };
  const onUp = (e: React.PointerEvent) => {
    if (mode.current !== 'drag') return;
    try { (e.currentTarget as Element).releasePointerCapture?.(e.pointerId); } catch { /* */ }
    const wasTap = dMoved.current < 6 && Date.now() - downAt.current < 350;
    mode.current = over.current ? 'hover' : 'idle';
    setHovering(over.current);
    if (wasTap) { vel.current = 0; onClick?.(); }
    run();
  };

  const spread = hovering ? FAN : 0;

  // One face's worth of artwork (both theme variants, frame-cycled by opacity).
  const faceArt = (isReal: boolean) => (
    <>
      {/* Light page → black rectangle */}
      {[1, 2, 3, 4].map((f) => (
        <img
          key={`d-${f}`}
          src={`/logos/${f}-dark.svg`}
          alt={isReal ? 'Prompt-Brush' : ''}
          aria-hidden={!isReal}
          draggable={false}
          className={`absolute inset-0 w-full h-full object-contain dark-logo-fix-light transition-opacity duration-150 ${
            frame === f ? 'opacity-100' : 'opacity-0'
          }`}
        />
      ))}
      {/* Dark page → white rectangle */}
      {[1, 2, 3, 4].map((f) => (
        <img
          key={`l-${f}`}
          src={`/logos/${f}-light.svg`}
          alt=""
          aria-hidden
          draggable={false}
          className={`absolute inset-0 w-full h-full object-contain dark-logo-fix-dark transition-opacity duration-150 ${
            frame === f ? 'opacity-100' : 'opacity-0'
          }`}
        />
      ))}
    </>
  );

  return (
    <div
      className="fixed top-4 left-4 w-[142px] h-[142px] cursor-grab active:cursor-grabbing relative z-[9999] select-none"
      style={{ marginTop: 'var(--pb-banner-h, 0px)', perspective: '850px', touchAction: 'none' }}
      onPointerEnter={onEnter}
      onPointerLeave={onLeave}
      onPointerDown={onDown}
      onPointerMove={onMove}
      onPointerUp={onUp}
    >
      <div
        ref={inner}
        className="relative w-full h-full"
        style={{ transform: 'scale(1.10) rotateY(0deg)', transformStyle: 'preserve-3d', willChange: 'transform' }}
      >
        {Array.from({ length: BLADES }).map((_, k) => {
          const isReal = k === REAL;
          // three solid pages fanning symmetrically around the centre; at rest they
          // stack into one page (spread 0), so there is no transparency at any point
          const offset = (k - REAL) * spread;
          return (
            <div
              key={k}
              className="absolute inset-0"
              style={{
                transform: `rotateY(${offset}deg)`,
                transformStyle: 'preserve-3d',
                transition: 'transform 420ms cubic-bezier(0.32, 0.04, 0.18, 1)',
              }}
            >
              {/* front face */}
              <div className="absolute inset-0" style={{ backfaceVisibility: 'hidden' }}>
                {faceArt(isReal)}
              </div>
              {/* back face — readable, so the logo never culls away mid-spin */}
              <div className="absolute inset-0" style={{ transform: 'rotateY(180deg)', backfaceVisibility: 'hidden' }}>
                {faceArt(isReal)}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
